import { Activity, TrendingUp, Sparkles, Fuel } from "lucide-react";
import { useSpotRate } from "@/hooks/useSpotRate";

interface AIMarketStatusBarProps {
  originLat?: number | null;
  originLon?: number | null;
  destLat?: number | null;
  destLon?: number | null;
  laneMiles?: number | null;
  equipmentType?: string | null;
}

export default function AIMarketStatusBar({
  originLat, originLon, destLat, destLon, laneMiles, equipmentType,
}: AIMarketStatusBarProps) {
  const { data: spotResult } = useSpotRate({
    originLat, originLon, destLat, destLon, laneMiles, equipmentType,
  });

  const confLabel = spotResult ? `${spotResult.confidencePercent}%` : "--";
  const demandLabel = spotResult
    ? (spotResult.confidence === "high" ? "High" : spotResult.confidence === "medium" ? "Moderate" : "Low")
    : "--";
  const capacityLabel = spotResult ? (spotResult.compCount >= 4 ? "Tight" : "Normal") : "--";
  const rpmLabel = spotResult ? `$${spotResult.marketRpm.toFixed(2)}/mi` : "--";

  const metrics = [
    { label: "Lane Demand", value: demandLabel, icon: TrendingUp, teal: true },
    { label: "Capacity", value: capacityLabel, icon: Activity, teal: false },
    { label: "AI Confidence", value: confLabel, icon: Sparkles, teal: true },
    { label: "MKT RPM", value: rpmLabel, icon: Fuel, teal: false },
  ];

  const LiveBadge = (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <span
        className="h-1.5 w-1.5 rounded-full animate-pulse"
        style={{ backgroundColor: "hsl(var(--brand-teal-deep))" }}
      />
      <span
        className="text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: "hsl(var(--brand-teal-deep))" }}
      >
        Live
      </span>
    </div>
  );

  return (
    <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-card border-b border-border">
      {/* Mobile header strip */}
      <div className="flex items-center justify-between mb-2 sm:hidden">
        <span
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: "hsl(var(--brand-teal-deep))" }}
        >
          AI Market
        </span>
        {LiveBadge}
      </div>

      <div className="flex items-center sm:justify-between">
        {/* Metrics: 2-col grid on mobile, single row on desktop */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 w-full sm:flex sm:items-center sm:gap-0 sm:w-auto">
          {metrics.map((m, i) => (
            <div key={m.label} className="flex items-center sm:contents">
              <div className="flex items-center gap-1.5 sm:px-2 min-w-0">
                <m.icon
                  className="h-3.5 w-3.5 flex-shrink-0"
                  style={{ color: m.teal ? "hsl(var(--brand-teal-deep))" : "hsl(var(--muted-foreground))" }}
                />
                <span className="text-[11px] font-medium text-muted-foreground truncate">
                  {m.label}
                </span>
                <span
                  className="text-[11px] font-bold ml-auto sm:ml-0"
                  style={{ color: m.teal ? "hsl(var(--brand-teal-deep))" : "hsl(var(--foreground))" }}
                >
                  {m.value}
                </span>
              </div>
              {i < metrics.length - 1 && (
                <span className="hidden sm:inline-block h-3 w-px bg-border" />
              )}
            </div>
          ))}
        </div>

        {/* Desktop-only Live badge on the right */}
        <div className="hidden sm:flex sm:ml-2">{LiveBadge}</div>
      </div>
    </div>
  );
}
